import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup, useReducedMotion } from 'motion/react';
import { useLanguage, CONFIG } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { CONTACT_EMAIL, CONTACT_WHATSAPP_URL } from '../config/contact';
import { buildOrderSummary } from '../utils/buildOrderSummary';
import { SPRING_DEFAULT, REDUCED_MOTION_TRANSITION } from '../utils/springs';
import './Cart.css';

/** ponytail: naive parse of product.weight strings; ml/L bottles return 0 kg. */
export function parseWeightKg(weightStr) {
  if (!weightStr) return 0;
  const match = String(weightStr).trim().match(/^(\d+(?:\.\d+)?)\s*(kg|g)$/i);
  if (!match) return 0;
  const value = parseFloat(match[1]);
  return match[2].toLowerCase() === 'g' ? value / 1000 : value;
}

export function lineWeightKg(item) {
  return parseWeightKg(item.weight) * (item.quantity || 1);
}

const Cart = () => {
  const { cart, removeFromCart, updateCartQuantity, getCartTotal } = useCart();
  const { language, formatPrice, t } = useLanguage();
  const reduceMotion = useReducedMotion();
  const itemTransition = reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_DEFAULT;
  const tap = reduceMotion ? undefined : { scale: 0.97 };
  const layoutProp = !reduceMotion ? 'position' : false;

  const shippingCost = CONFIG.SHIPPING_COST_EUR;

  const totals = useMemo(() => {
    if (cart.length === 0) {
      return null;
    }
    const subtotal = getCartTotal();
    const freeShippingThresholdKg = CONFIG.FREE_SHIPPING_THRESHOLD_KG;
    const totalWeightKg = cart.reduce((sum, item) => sum + lineWeightKg(item), 0);
    const qualifiesForFreeShipping = totalWeightKg >= freeShippingThresholdKg;
    const shippingAmount = qualifiesForFreeShipping ? 0 : shippingCost;
    const total = subtotal + shippingAmount;
    return {
      subtotal,
      totalWeightKg,
      qualifiesForFreeShipping,
      shippingAmount,
      total,
      remainingKg: Math.max(0, freeShippingThresholdKg - totalWeightKg),
      progressRatio: Math.min(1, totalWeightKg / freeShippingThresholdKg)
    };
  }, [cart, getCartTotal, shippingCost]);

  const orderSummaryText = useMemo(() => {
    if (!totals) return '';
    return buildOrderSummary({
      cart,
      language,
      formatPrice,
      subtotal: totals.subtotal,
      shippingAmount: totals.shippingAmount,
      total: totals.total,
      labels: {
        heading: t('cartPage.orderSummaryHeading'),
        subtotal: t('cartPage.subtotal'),
        shipping: t('cartPage.shipping'),
        free: t('cartPage.free'),
        total: t('cartPage.total'),
        seller: t('cartPage.orderSellerLabel'),
        sellerName: t('cartPage.orderSellerName'),
        pickup: t('cartPage.orderPickupLabel'),
        pickupPlace: t('cartPage.orderPickupPlace')
      }
    });
  }, [cart, language, formatPrice, totals, t]);

  const whatsappCheckoutHref = `${CONTACT_WHATSAPP_URL}?text=${encodeURIComponent(orderSummaryText)}`;
  const emailCheckoutHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    t('cartPage.checkoutEmailSubject')
  )}&body=${encodeURIComponent(orderSummaryText)}`;

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <h1 className="page-title">{t('cartPage.title')}</h1>
          <div className="empty-cart">
            <p>{t('cartPage.empty')}</p>
            <Link to="/products" className="continue-shopping-btn btn-primary">
              {t('cartPage.continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const {
    subtotal,
    qualifiesForFreeShipping,
    remainingKg,
    progressRatio,
    shippingAmount,
    total: grandTotal
  } = totals;

  const remainingKgDisplay =
    remainingKg % 1 === 0 ? String(remainingKg) : remainingKg.toFixed(1);

  const getItemName = (item) => {
    if (language === 'ro' && item.nameRo) {
      return item.nameRo;
    }
    return item.name;
  };

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="page-title">{t('cartPage.title')}</h1>

        <div className="cart-content">
          <LayoutGroup>
            <div className="cart-items">
              <AnimatePresence initial={false}>
                {cart.map((item) => (
                  <motion.div
                    key={item.id}
                    className="cart-item"
                    layout={layoutProp}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={itemTransition}
                  >
                    <div className="cart-item-image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="cart-product-img" />
                      ) : (
                        <div className="cart-product-placeholder">{item.name.charAt(0)}</div>
                      )}
                    </div>
                    <div className="cart-item-info">
                      <h3>{getItemName(item)}</h3>
                      <p className="cart-item-weight">{item.weight}</p>
                      <p className="cart-item-price">{formatPrice(item.price)}</p>
                    </div>
                    <div className="cart-item-quantity">
                      <motion.button
                        type="button"
                        className="quantity-btn"
                        aria-label={t('cartPage.qtyDecrease')}
                        onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        whileTap={tap}
                      >
                        −
                      </motion.button>
                      <span className="quantity-value">{item.quantity}</span>
                      <motion.button
                        type="button"
                        className="quantity-btn"
                        aria-label={t('cartPage.qtyIncrease')}
                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        whileTap={tap}
                      >
                        +
                      </motion.button>
                    </div>
                    <div className="cart-item-total">
                      <span>{formatPrice(item.price * item.quantity)}</span>
                    </div>
                    <motion.button
                      type="button"
                      onClick={() => removeFromCart(item.id)}
                      className="remove-btn"
                      aria-label={t('cartPage.removeItem')}
                      whileTap={tap}
                    >
                      ×
                    </motion.button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </LayoutGroup>

          <div className="cart-summary">
            <div className="shipping-info">
              {qualifiesForFreeShipping ? (
                <div className="free-shipping">
                  <span>✓</span> {t('cartPage.freeShipping')}
                </div>
              ) : (
                <div className="shipping-progress">
                  <p>
                    {t('cartPage.addPrefix')}
                    <strong>{remainingKgDisplay} kg</strong> {t('cartPage.addMore')}
                  </p>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ transform: `scaleX(${progressRatio})` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="cart-totals">
              <div className="total-row">
                <span>{t('cartPage.subtotal')}</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="total-row">
                <span>{t('cartPage.shipping')}</span>
                <span>
                  {qualifiesForFreeShipping ? t('cartPage.free') : formatPrice(shippingAmount)}
                </span>
              </div>
              <div className="total-row final-total">
                <span>{t('cartPage.total')}</span>
                <span>{formatPrice(grandTotal)}</span>
              </div>
            </div>

            <a
              href={whatsappCheckoutHref}
              className="checkout-btn btn-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('cartPage.checkoutWhatsApp')}
            </a>
            <a href={emailCheckoutHref} className="checkout-btn checkout-btn--secondary">
              {t('cartPage.checkoutEmail')}
            </a>

            <Link to="/products" className="continue-shopping-link">
              {t('cartPage.continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
